import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Plus, Trash2, Save, FileText, Mail, Download } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["Pending Invoice", "Invoice Sent", "Awaiting Payment", "Paid", "Preparing", "Shipped", "Delivered", "Cancelled", "Refunded"];

/**
 * AdminOrderDetail
 * Full-page editor for a single order: edit items (qty/price/variant), notes, shipping notes,
 * change status, generate/send invoice PDF, and download the current invoice.
 */
export default function AdminOrderDetail() {
  const { orderId } = useParams();
  const nav = useNavigate();
  const [order, setOrder] = useState(null);
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [status, setStatus] = useState("");
  const [tracking, setTracking] = useState("");
  const [notes, setNotes] = useState("");
  const [internalNotes, setInternalNotes] = useState("");
  const [shippingNotes, setShippingNotes] = useState("");
  const [shipAddr, setShipAddr] = useState({});
  const [addSlug, setAddSlug] = useState("");
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);

  const load = () => {
    api.get(`/admin/orders/${orderId}`).then((r) => {
      const o = r.data;
      setOrder(o);
      setItems((o.items || []).map((it) => ({ ...it })));
      setStatus(o.status || "");
      setTracking(o.tracking_number || "");
      setNotes(o.notes || "");
      setInternalNotes(o.internal_notes || "");
      setShippingNotes(o.shipping_notes || "");
      setShipAddr(o.shipping_address || {});
    }).catch(() => toast.error("Order not found"));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [orderId]);
  useEffect(() => { api.get("/admin/products").then((r) => setProducts(r.data || [])).catch(() => {}); }, []);

  const subtotal = useMemo(() => items.reduce((s, i) => s + Number(i.unit_price || 0) * Number(i.quantity || 0), 0), [items]);
  const discount = Number(order?.discount || 0);
  const shippingCost = Number(order?.shipping_cost || 0);
  const total = Math.max(0, subtotal - discount + shippingCost);

  const updateItem = (idx, patch) => {
    setItems((arr) => arr.map((it, i) => i === idx ? { ...it, ...patch } : it));
  };
  const removeLine = (idx) => setItems((arr) => arr.filter((_, i) => i !== idx));

  const addLine = () => {
    if (!addSlug) return;
    const p = products.find((x) => x.slug === addSlug);
    if (!p) return;
    const def = (p.variants || []).find((v) => v.default && v.active !== false) || (p.variants || [])[0];
    if (!def) { toast.error("Product has no active variant"); return; }
    setItems((arr) => [...arr, {
      peptide_slug: p.slug, name: p.name, image_url: p.image_url,
      variant_id: def.id, variant_label: def.label,
      quantity: 1, unit_price: Number(def.price || 0),
    }]);
    setAddSlug("");
  };

  const changeVariant = (idx, variant_id) => {
    const line = items[idx];
    const p = products.find((x) => x.slug === line.peptide_slug);
    if (!p) return;
    const v = (p.variants || []).find((x) => x.id === variant_id);
    if (!v) return;
    updateItem(idx, { variant_id: v.id, variant_label: v.label, unit_price: Number(v.price || 0) });
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/orders/${orderId}/edit`, {
        items, notes, internal_notes: internalNotes, shipping_notes: shippingNotes,
        shipping_address: shipAddr,
      });
      if (status !== order.status || tracking !== (order.tracking_number || "")) {
        await api.patch(`/admin/orders/${orderId}`, { status, tracking_number: tracking });
      }
      toast.success("Saved");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const generateInvoice = async () => {
    setGenerating(true);
    try {
      const r = await api.post(`/admin/orders/${orderId}/generate-invoice`);
      toast.success(`Invoice ${r.data.invoice_number} generated and emailed`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to generate invoice");
    } finally {
      setGenerating(false);
    }
  };

  if (!order) return <div className="p-8 text-sm text-slate-500">Loading…</div>;

  return (
    <div data-testid="admin-order-detail" className="max-w-6xl">
      <Link to="/admin/orders" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-4"><ArrowLeft className="w-4 h-4"/> All orders</Link>

      <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="eyebrow mb-2">Order</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">{order.order_id}</h1>
          <div className="text-sm text-slate-600 mt-1">{order.customer_name} · {order.customer_email} · Placed {new Date(order.placed_at).toLocaleDateString()}</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Badge className="rounded-md font-mono text-[10px] uppercase tracking-widest bg-blue-50 text-blue-700 border border-blue-200">{order.status}</Badge>
          {order.invoice_number && (
            <a href={`/api/invoices/${order.invoice_number}/pdf`} target="_blank" rel="noreferrer">
              <Button variant="outline" className="border-slate-300"><Download className="w-4 h-4 mr-1.5" strokeWidth={1.75}/> Download {order.invoice_number}</Button>
            </a>
          )}
          <Button data-testid="generate-invoice-btn" disabled={generating} onClick={generateInvoice} className="bg-slate-900 hover:bg-blue-600 text-white">
            <FileText className="w-4 h-4 mr-1.5" strokeWidth={1.75}/> {order.invoice_number ? "Regenerate + Resend" : "Generate Invoice"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
        <div className="space-y-6">
          <section className="border border-slate-200 rounded-md bg-white">
            <header className="px-5 py-3 border-b border-slate-100 font-display text-lg font-semibold">Items</header>
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs font-mono uppercase tracking-widest text-slate-500">
                <tr><th className="text-left px-4 py-2">Product</th><th className="text-left px-4 py-2">Dose</th><th className="text-right px-4 py-2 w-20">Qty</th><th className="text-right px-4 py-2 w-28">Unit</th><th className="text-right px-4 py-2 w-28">Line</th><th className="w-10"></th></tr>
              </thead>
              <tbody>
                {items.map((it, idx) => {
                  const prod = products.find((p) => p.slug === it.peptide_slug);
                  const variants = (prod?.variants || []).filter((v) => v.active !== false);
                  return (
                    <tr key={idx} data-testid={`admin-order-line-${idx}`} className="border-b border-slate-100 last:border-b-0">
                      <td className="px-4 py-2 text-slate-900">{it.name}</td>
                      <td className="px-4 py-2">
                        {variants.length > 0 ? (
                          <select
                            value={it.variant_id || ""}
                            onChange={(e) => changeVariant(idx, e.target.value)}
                            data-testid={`admin-order-variant-${idx}`}
                            className="h-8 px-2 rounded-md border border-slate-300 text-sm w-full"
                          >
                            {variants.map((v) => <option key={v.id} value={v.id}>{`${v.label} · $${Number(v.price).toFixed(2)}`}</option>)}
                          </select>
                        ) : (
                          <div className="text-xs text-slate-500 font-mono">{it.variant_label || "—"}</div>
                        )}
                      </td>
                      <td className="px-4 py-2 text-right">
                        <Input type="number" min="1" value={it.quantity} onChange={(e) => updateItem(idx, { quantity: Number(e.target.value) })} data-testid={`admin-order-qty-${idx}`} className="h-8 text-right font-mono"/>
                      </td>
                      <td className="px-4 py-2 text-right">
                        <Input type="number" step="0.01" value={it.unit_price} onChange={(e) => updateItem(idx, { unit_price: Number(e.target.value) })} data-testid={`admin-order-price-${idx}`} className="h-8 text-right font-mono"/>
                      </td>
                      <td className="px-4 py-2 text-right font-mono">${(Number(it.unit_price) * Number(it.quantity)).toFixed(2)}</td>
                      <td className="px-4 py-2 text-right">
                        <button onClick={() => removeLine(idx)} className="p-1 rounded hover:bg-red-50 text-red-500" data-testid={`admin-order-remove-${idx}`}><Trash2 className="w-3.5 h-3.5" strokeWidth={1.75}/></button>
                      </td>
                    </tr>
                  );
                })}
                <tr>
                  <td colSpan={6} className="px-4 py-3 bg-slate-50">
                    <div className="flex items-center gap-2">
                      <select value={addSlug} onChange={(e) => setAddSlug(e.target.value)} data-testid="admin-order-add-select" className="h-9 px-2 rounded-md border border-slate-300 text-sm">
                        <option value="">Add product…</option>
                        {products.map((p) => <option key={p.slug} value={p.slug}>{p.name}</option>)}
                      </select>
                      <Button data-testid="admin-order-add-btn" onClick={addLine} className="bg-slate-900 hover:bg-blue-600 text-white h-9"><Plus className="w-4 h-4 mr-1"/> Add line</Button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </section>

          <section className="border border-slate-200 rounded-md bg-white">
            <header className="px-5 py-3 border-b border-slate-100 font-display text-lg font-semibold">Shipping address</header>
            <div className="p-5 grid grid-cols-2 gap-3">
              {["first_name", "last_name", "company", "line1", "line2", "city", "state", "postal_code", "country", "phone", "email"].map((k) => (
                <Input key={k} placeholder={k} value={shipAddr[k] || ""} onChange={(e) => setShipAddr({...shipAddr, [k]: e.target.value})} className={k === "line1" || k === "line2" || k === "company" ? "col-span-2" : ""}/>
              ))}
            </div>
          </section>

          <section className="border border-slate-200 rounded-md bg-white">
            <header className="px-5 py-3 border-b border-slate-100 font-display text-lg font-semibold">Notes</header>
            <div className="p-5 space-y-3">
              <div>
                <label className="text-xs font-mono uppercase tracking-widest text-slate-500">Customer notes (visible to customer)</label>
                <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="admin-order-notes"/>
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-widest text-slate-500">Shipping notes</label>
                <Textarea rows={2} value={shippingNotes} onChange={(e) => setShippingNotes(e.target.value)} data-testid="admin-order-shipping-notes"/>
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-widest text-slate-500">Internal notes (never emailed)</label>
                <Textarea rows={3} value={internalNotes} onChange={(e) => setInternalNotes(e.target.value)} data-testid="admin-order-internal-notes"/>
              </div>
            </div>
          </section>
        </div>

        <aside className="lg:sticky lg:top-4 lg:h-fit space-y-4">
          <section className="border border-slate-200 rounded-md bg-white p-5">
            <div className="font-display text-base font-semibold mb-3">Status</div>
            <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="admin-order-status" className="w-full h-10 px-3 rounded-md border border-slate-300 text-sm mb-2">
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <label className="text-xs font-mono uppercase tracking-widest text-slate-500">Tracking</label>
            <Input value={tracking} onChange={(e) => setTracking(e.target.value)} data-testid="admin-order-tracking"/>
          </section>

          <section className="border border-slate-200 rounded-md bg-white p-5">
            <div className="font-display text-base font-semibold mb-3">Totals</div>
            <div className="space-y-1.5 text-sm">
              <Row label="Subtotal" value={`$${subtotal.toFixed(2)}`}/>
              {discount > 0 && <Row label={`Discount${order.coupon_code ? ` (${order.coupon_code})` : ""}`} value={`−$${discount.toFixed(2)}`} negative/>}
              <Row label={`Shipping${order.shipping_label ? ` · ${order.shipping_label}` : ""}`} value={`$${shippingCost.toFixed(2)}`}/>
              <div className="border-t border-slate-100 pt-2 mt-2 flex items-baseline justify-between">
                <div className="font-display font-semibold">Grand total</div>
                <div className="font-mono text-lg font-semibold">${total.toFixed(2)}</div>
              </div>
            </div>
          </section>

          <Button data-testid="admin-order-save" disabled={saving} onClick={saveAll} className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white"><Save className="w-4 h-4 mr-1.5" strokeWidth={1.75}/> Save changes</Button>
          {order.invoice_sent_at && <div className="text-[11px] text-slate-500 text-center flex items-center justify-center gap-1"><Mail className="w-3 h-3"/> Invoice emailed {new Date(order.invoice_sent_at).toLocaleString()}</div>}
        </aside>
      </div>
    </div>
  );
}

function Row({ label, value, negative }) {
  return (
    <div className="flex items-baseline justify-between">
      <div className="text-slate-600">{label}</div>
      <div className={`font-mono ${negative ? "text-blue-700" : "text-slate-900"}`}>{value}</div>
    </div>
  );
}
